import { resize, ResizeMode } from '@wix/thumbnail-generator';
import { MediaTypes, parentIncomingActions, VideoMakerOpener } from '@wix/video-maker-opener';
import { all, has, head, isEmpty, map, pipe } from 'ramda';
import { ensureArray } from 'ramda-adjunct';
import React, { createContext, FC, useCallback, useContext, useEffect, useMemo } from 'react';
import { Subject } from 'rxjs';
import { BiAddMediaActionTarget, OpenSource } from '../../../constants/Bi';
import { MainSource } from '../../../constants/Source';
import { extractCommonBiFieldsFromItems } from '../../bi';
import { getId } from '../../common';
import { getLocale } from '../../env/getLocale';
import { useMMGRSettings } from '../../hoc/withMMGRSettings';
import {
  IExtendedVideoMakerParams,
  initializeVideoMakerOpenerParams,
  videoMakerLocale,
} from '../../hoc/withVideoMaker/utils';
import { MediaManagerSDKAnyItem, MediaManagerSDKFile } from '../../media-manager-sdk';
import { canEditItemInVideoMaker, canUseItemInVideoMaker } from '../../media-manager-sdk/items';
import { isVideoMediaType } from '../../mediaType';
import { isNonSavedSite } from '../../siteMediaToken';
import { withProtocol } from '../../url';
import { useFileType } from '../bi/useFileType';
import { useTrackAddMediaUploadProcessAnalytics } from '../bi/useTrackAddMediaUploadProcessAnalytics';
import { useStorageQuotaAwareHandler } from '../storageQuota/useStorageQuotaAwareHandler';
import { StorageQuotaUseCase } from '../storageQuota/useStorageQuotaAwareHandler/StorageQuotaUseCase';
import { useAdapter } from '../useAdapter';
import { useParsedSiteMediaToken } from '../useParsedSiteMediaToken';

export const useCanUseItemsInVideoMaker = (items: MediaManagerSDKAnyItem | MediaManagerSDKAnyItem[]) => {
  const itemsArray = ensureArray<MediaManagerSDKAnyItem>(items);

  const { mmgrSettings } = useMMGRSettings();
  const { isSecureContext, openSource, metaSiteToken } = mmgrSettings;
  const { metaSiteId } = useParsedSiteMediaToken();

  return (
    metaSiteToken &&
    !isNonSavedSite(metaSiteId) &&
    !isSecureContext &&
    openSource !== OpenSource.VideoMaker &&
    all(canUseItemInVideoMaker, itemsArray)
  );
};

const videoMetadataSubject = new Subject();

const VideoMakerContext = createContext<{
  videoMaker: VideoMakerOpener;
  openVideoMaker: (files?: MediaManagerSDKFile | MediaManagerSDKFile[]) => void;
}>({
  videoMaker: null,
  openVideoMaker: () => {},
});

export const VideoMakerProvider: FC = ({ children }) => {
  const { mmgrSettings } = useMMGRSettings();
  const { siteMediaToken, origin, metaSiteToken: signedInstance } = mmgrSettings;
  const { metaSiteId, userId } = useParsedSiteMediaToken();

  const videoMaker = useMemo(() => new VideoMakerOpener(), []);

  const instanceGetter = () => signedInstance;

  const _openVideoMaker = useCallback(
    (extendedVideoMakerParams?: IExtendedVideoMakerParams) => {
      videoMaker.withInstanceGetter(instanceGetter).open(
        initializeVideoMakerOpenerParams({
          locale: getLocale() as videoMakerLocale,
          metaSiteId,
          origin,
          siteMediaToken,
          userId,
          extendedVideoMakerParams,
        }),
      );
    },
    [metaSiteId, origin, siteMediaToken, userId],
  );

  const openVideoMaker = useCallback(
    (files?: MediaManagerSDKFile | MediaManagerSDKFile[]) => {
      const filesArray = files ? ensureArray<MediaManagerSDKFile>(files) : [];

      const openerParams = isEmpty(files)
        ? {}
        : canEditItemInVideoMaker(filesArray)
        ? {
            fileId: pipe<MediaManagerSDKAnyItem[], MediaManagerSDKAnyItem, string>(head, getId)(filesArray),
          }
        : {
            videoData: {
              sources: map(({ id, mediaType, thumbnailUrl, videoPreviewUrls, originalUrl }) => {
                const isVideoItem = isVideoMediaType(mediaType);

                return {
                  id,
                  mediaType: mediaType as unknown as MediaTypes,
                  url: isVideoItem
                    ? withProtocol(head(videoPreviewUrls))
                    : withProtocol(resize(originalUrl, { width: 1000, height: 1000, mode: ResizeMode.Fit })),
                  thumbnailUrl: isVideoItem ? withProtocol(thumbnailUrl) : undefined,
                };
              }, filesArray),
            },
          };

      _openVideoMaker(openerParams);
    },
    [_openVideoMaker],
  );

  const privateSiteAdapter = useAdapter(MainSource.SiteFiles);
  const { trackAddMediaFinished } = useTrackAddMediaUploadProcessAnalytics();
  const fileType = useFileType();

  useEffect(() => {
    if (!videoMaker) {
      return;
    }

    return videoMaker.on(parentIncomingActions.videoSavedToMediaManager, ({ payload }) => {
      privateSiteAdapter.item.get(payload.fileId).then(({ data }) => {
        const { correlationId, loadingTime } = payload;

        videoMetadataSubject.next(data);

        trackAddMediaFinished({
          success: true,
          batch: false,
          batchSize: 1,
          target: BiAddMediaActionTarget.VideoMaker,
          correlationId,
          isTags: has('tags', data) && !isEmpty((data as MediaManagerSDKAnyItem).tags),
          ...extractCommonBiFieldsFromItems([data], { fileType }),
          loadingTime,
        });
      });
    });
  }, [videoMaker, privateSiteAdapter, trackAddMediaFinished, fileType]);

  const storageQuotaAware = useStorageQuotaAwareHandler();

  return (
    <VideoMakerContext.Provider
      value={{ videoMaker, openVideoMaker: storageQuotaAware(openVideoMaker, StorageQuotaUseCase.VIDEO_MAKER) }}
    >
      {children}
    </VideoMakerContext.Provider>
  );
};

export const useVideoMaker = () => useContext(VideoMakerContext);

export const useVideoMakerOnFileUploaded = (onFileUploaded = (_file) => {}) => {
  useEffect(() => {
    const subscription = videoMetadataSubject.subscribe(onFileUploaded);

    return () => subscription.unsubscribe();
  }, [onFileUploaded]);
};
